import type { CalcResult, Category } from '../types';
import { fmtCeil, fmtPct } from '../lib/format';
import { recommendedRange } from '../lib/calc';

interface Props {
  category: Category;
  result: CalcResult;
  emp3: number;
}

const REQUIREMENT_LABEL: Record<Category, string> = {
  A_no: '加点要件なし（成長率判定なし）',
  A_yes: '要件: 3.0%以上',
  B: '要件: 3.5%以上（未達は返還リスク）',
};

export function ResultSummary({ category, result, emp3 }: Props) {
  const { requiredRate, cagr3, targetPerEmp3, targetSalary3, growthJudge, wageJudge } = result;
  const range = recommendedRange(category);
  const rangeLabel = `${(range.lower * 100).toFixed(1)}%〜${(range.upper * 100).toFixed(1)}%`;

  const reqRateCardClass = 'result-card';
  let actualRateCardClass = 'result-card';
  let targetCardClass = 'result-card';
  let growthBox: { cls: string; ttl: JSX.Element; desc: JSX.Element };

  if (category === 'A_no') {
    growthBox = {
      cls: 'judge-box warn',
      ttl: (
        <>
          <i className="fas fa-info-circle" /> 加点希望なし
        </>
      ),
      desc: (
        <>
          賃金引上げ加点を希望しないため、年平均成長率の達成判定はありません。Step 13の「事業場内最低賃金の引上げ計画を、従業員へ表明しましたか？」で <b>「いいえ」</b> を選択してください。
        </>
      ),
    };
  } else if (growthJudge === 'OK') {
    actualRateCardClass = 'result-card ok';
    targetCardClass = 'result-card ok';
    growthBox = {
      cls: 'judge-box ok',
      ttl: (
        <>
          <i className="fas fa-check-circle" /> 達成 — 年平均成長率 {fmtPct(cagr3)}% ≧ 必要成長率 {(requiredRate * 100).toFixed(1)}%
        </>
      ),
      desc: <>入力された計画値で、必要な年平均成長率を満たしています。</>,
    };
  } else {
    actualRateCardClass = 'result-card ng';
    targetCardClass = 'result-card ng';
    const shortfall = requiredRate * 100 - cagr3;
    growthBox = {
      cls: 'judge-box ng',
      ttl: (
        <>
          <i className="fas fa-times-circle" /> 未達 — 年平均成長率 {fmtPct(cagr3)}% &lt; 必要成長率 {(requiredRate * 100).toFixed(1)}%（不足 約{fmtPct(shortfall)}%）
        </>
      ),
      desc: (
        <>
          3年度目の1人当たり給与支給総額を <b>{fmtCeil(targetPerEmp3)}円</b> 以上にする必要があります。
          <br />
          3年度目の給与支給総額（従業員数 {emp3} 人想定）：<b>{fmtCeil(targetSalary3)}円</b> 以上。
        </>
      ),
    };
  }

  let diffCardClass = 'result-card';
  let addonCardClass = 'result-card';
  let addonText = '—';
  let wageBox: { cls: string; ttl: JSX.Element; desc: JSX.Element };

  switch (wageJudge.kind) {
    case 'unable':
      diffCardClass = 'result-card ng';
      addonCardClass = 'result-card ng';
      addonText = '✗ 申請不可';
      wageBox = {
        cls: 'judge-box ng',
        ttl: (
          <>
            <i className="fas fa-times-circle" /> 申請要件違反
          </>
        ),
        desc: (
          <>
            事業場内最低賃金が地域別最低賃金を下回っています。<b>本補助金は申請不可</b>です。
          </>
        ),
      };
      break;
    case 'no_addon':
      diffCardClass = 'result-card warn';
      addonText = '加点なし';
      wageBox = {
        cls: 'judge-box warn',
        ttl: (
          <>
            <i className="fas fa-exclamation-circle" /> 賃金加点なし（差額 {wageJudge.diff}円）
          </>
        ),
        desc: (
          <>
            申請要件は満たしていますが、賃金引上げ加点（+30円）の対象外です。あと <b>{30 - wageJudge.diff}円</b> で加点条件を満たします。
          </>
        ),
      };
      break;
    case 'plus_30':
      diffCardClass = 'result-card ok';
      addonCardClass = 'result-card ok';
      addonText = '+30円加点';
      wageBox = {
        cls: 'judge-box ok',
        ttl: (
          <>
            <i className="fas fa-check-circle" /> 賃金加点 +30円段階（差額 {wageJudge.diff}円）
          </>
        ),
        desc: (
          <>
            事業場内最低賃金が地域別最低賃金 +30円以上となり、加点対象です。さらに <b>{50 - wageJudge.diff}円</b> で +50円段階の追加加点が得られます。
          </>
        ),
      };
      break;
    case 'plus_50':
      diffCardClass = 'result-card ok';
      addonCardClass = 'result-card ok';
      addonText = '+50円加点（最高）';
      wageBox = {
        cls: 'judge-box ok',
        ttl: (
          <>
            <i className="fas fa-check-circle" /> 賃金加点 +50円段階（差額 {wageJudge.diff}円）
          </>
        ),
        desc: <>事業場内最低賃金が地域別最低賃金 +50円以上となり、最高位の加点対象です。</>,
      };
      break;
  }

  const diffSign = wageJudge.diff >= 0 ? '+' : '';

  return (
    <div className="section">
      <h2>
        <span className="num">5</span>計算結果サマリー
      </h2>

      <h3>① 給与支給総額の年平均成長率（達成判定）</h3>
      <div className="result-grid">
        <div className={reqRateCardClass}>
          <div className="ttl">推奨計画レンジ（年平均成長率）</div>
          <div className="val">
            {rangeLabel}
          </div>
          <div className="hint" style={{ marginTop: '0.3rem' }}>
            <small>{REQUIREMENT_LABEL[category]}</small>
          </div>
        </div>
        <div className={actualRateCardClass}>
          <div className="ttl">3年度目までの実際の成長率</div>
          <div className="val">
            {category === 'A_no' ? '—' : fmtPct(cagr3)}
            <span className="small">%</span>
          </div>
        </div>
        <div className={targetCardClass}>
          <div className="ttl">3年度目の最低必要1人当たり給与</div>
          <div className="val">
            {fmtCeil(targetPerEmp3)}
            <span className="small">円</span>
          </div>
        </div>
      </div>

      <div className={growthBox.cls}>
        <div className="ttl">{growthBox.ttl}</div>
        <div className="desc">{growthBox.desc}</div>
      </div>

      <h3>② 事業場内最低賃金の加点判定</h3>
      <div className="result-grid">
        <div className={diffCardClass}>
          <div className="ttl">地域別最低賃金との差額</div>
          <div className="val">
            {diffSign}
            {wageJudge.diff}
            <span className="small">円</span>
          </div>
        </div>
        <div className={addonCardClass}>
          <div className="ttl">加点段階</div>
          <div className="val">{addonText}</div>
        </div>
      </div>

      <div className={wageBox.cls}>
        <div className="ttl">{wageBox.ttl}</div>
        <div className="desc">{wageBox.desc}</div>
      </div>

      <h3>③ 実質取得加点</h3>
      <div className="judge-box effective">
        <div className="ttl">
          <i className="fas fa-award" /> {result.effective}
        </div>
      </div>
    </div>
  );
}
